import React, { useState } from 'react';
import { DialogContent } from '@material-ui/core';
import { IUserSession } from '../../../../@types/employee';
import { IValueAddedTax } from '../../../../@types/model/masterData/valueAddedTax/valueAddedTax';
import { IValueAddedTaxFormValues } from '../../../../@types/model/masterData/valueAddedTax/valueAddedTaxFormValues';
import ValueAddedTaxModelHelper from '../../../../@types/model/masterData/valueAddedTax/valueAddedTaxModelHelper';
import GeneralFunctions from '../../../../store/general/functions';
import ValueAddedTaxEditForm from './ValueAddedTaxForm';
import moment from 'moment';
import firebase from 'firebase';
import lodash from 'lodash';
import ProjectDialog from '../../../customComponentV2/dialog/ProjectDialog';

interface IValueAddedTaxEditDialogProps {
    currentUser ?: IUserSession | null;
    selectedValueAddedTax : IValueAddedTax | null;
    isOpen : boolean;
    onDismiss : () => void;
    sortedRows : Array<IValueAddedTax>;
    valueAddedTaxes : Array<IValueAddedTax>;
}

const ValueAddedTaxEditDialog = (props : IValueAddedTaxEditDialogProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    /*================================================================================================================
     *                                                      Effects
     * ==============================================================================================================*/

    /*================================================================================================================
    *                                                  Handler Methods
    * ==============================================================================================================*/

    const onSubmit = async (formValues : IValueAddedTaxFormValues) : Promise<void> => {
        if (!props.currentUser) {
            setIsLoading(false);
            return;
        }
        
        const existingValueAddedTax = props.valueAddedTaxes.find(x => x.guid === formValues.guid);
        let previousValueAddedTaxPeriod;
        if (existingValueAddedTax) 
        {
            previousValueAddedTaxPeriod = lodash.chain(props.sortedRows)
                .filter(x => x.isActive && x.guid !== formValues.guid)
                .last()
                .value();
        }
        else 
        {
            previousValueAddedTaxPeriod = lodash.chain(props.valueAddedTaxes)
                .filter(x => x.isActive)
                .orderBy(x => x.startDate, 'desc')
                .first()
                .value();
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (previousValueAddedTaxPeriod)
        {
            const previousVATStartDay = moment(previousValueAddedTaxPeriod.startDate.toMillis()).utc().startOf('day');
            const previousVATEndDay = previousValueAddedTaxPeriod.endDate ? moment(previousValueAddedTaxPeriod.endDate.toMillis()).utc().startOf('day') : null;
            const formValuesStartDay = formValues.startDate.utc().startOf('day');
            const newEndDate = formValues.startDate.clone().subtract(1, 'day').utc().startOf('day');

            if (formValuesStartDay.isSameOrBefore(previousVATStartDay))
            {
                GeneralFunctions.generalShowErrorSnackbar('Invalid Value Added Tax Start Date');
                return;
            }

            if (previousVATEndDay &&
                newEndDate.isSameOrBefore(previousVATStartDay))
            {
                GeneralFunctions.generalShowErrorSnackbar('Invalid Value Added Tax Start Date');
                return;
            }

            if (previousVATStartDay.isSameOrBefore(newEndDate)) 
            {
                previousValueAddedTaxPeriod.endDate = firebase.firestore.Timestamp.fromMillis(newEndDate.valueOf());
            }
            else
            {
                GeneralFunctions.generalShowErrorSnackbar('Invalid Value Added Tax Start Date');
                return;
            }
        }

        const valueAddedTax = ValueAddedTaxModelHelper.createSave(props.currentUser, formValues, existingValueAddedTax);

        try {
            setIsLoading(true);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (previousValueAddedTaxPeriod)
            {
                await ValueAddedTaxModelHelper.save(previousValueAddedTaxPeriod);
            }
            await ValueAddedTaxModelHelper.save(valueAddedTax);
            setIsLoading(false);
            GeneralFunctions.generalShowSuccessSnackbar('Value Added Tax updated successfully.');
            props.onDismiss();
        } catch (e) {
            GeneralFunctions.generalShowSuccessSnackbar('Error updating Value Added Tax.');
            setIsLoading(false);
            props.onDismiss();
        }
    };

    const getInitialFormValues = () : IValueAddedTaxFormValues => {
        return ValueAddedTaxModelHelper.createFormValues(props.selectedValueAddedTax);
    };

    /*================================================================================================================
    *                                                  Memos
    * ==============================================================================================================*/

    /*================================================================================================================
    *                                                  Render
    * ==============================================================================================================*/

    return (
        <>
            <ProjectDialog
                title={props.selectedValueAddedTax ? 'Edit Value Added Tax' : 'Add Value Added Tax'}
                isOpen={props.isOpen}
                isLoadingCircular={isLoading}
                onClose={props.onDismiss}
                maxWidth={'sm'}>
                <DialogContent className='fdc flx1 jcfs hfill'>
                    <ValueAddedTaxEditForm 
                        initialValues={getInitialFormValues()} 
                        onSubmit={onSubmit} 
                        onCancel={props.onDismiss}
                    />
                </DialogContent>
            </ProjectDialog>
        </>
    );
};

export default ValueAddedTaxEditDialog;